/**
 * Utility functions for checking if annotations are saved and saving annotations
 */

import ActivityService from '../../../../platform/app/src/services/ActivityService';
import {
  extractAnnotationIdFromSaveResponse,
  fetchAnnotationIdForGrader,
} from '../../../../platform/app/src/utils/annotationIdFromResponse';
import AuthService from '../../../../platform/app/src/services/AuthService';
import { isLoggedInUserStudyGraderSync } from '../../../../platform/app/src/utils/graderStudyViewOnly';
import {
  getActiveViewerStudyUid,
  isAnnotationResponseForStudyUid,
} from '../../../../platform/app/src/utils/viewerStudySession';

/**
 * Checks if an annotation with the given UID was already saved in the database
 * @param annotationUID - The annotation UID to check
 * @returns true if the annotation exists in saved data, false otherwise
 */
function resolveEffectiveGraderIdForUnsavedCheck(): string | null {
  let effectiveGraderId: string | null =
    (window as any).currentUserGraderId != null ? String((window as any).currentUserGraderId) : null;

  if (effectiveGraderId == null) {
    try {
      const currentUser = AuthService.getInstance().getCurrentUser();
      if (currentUser?.id != null) {
        effectiveGraderId = String(currentUser.id);
      }
    } catch {
      // ignore
    }
  }

  return effectiveGraderId;
}

/** Clears in-session edit tracking after a successful save or study change. */
export function clearModifiedAnnotationUIDs(): void {
  const modified = (window as any).modifiedAnnotationUIDs as Set<string> | undefined;
  modified?.clear();
}

/** Clears the viewer tracking indicator after annotations have been persisted. */
export function clearAnnotationTrackingState(): void {
  clearModifiedAnnotationUIDs();
  try {
    (window as any).__clearTrackedMeasurements?.();
  } catch (error) {
    console.warn('[annotationSaveUtils] Error clearing annotation tracking state:', error);
  }
}

function isAnnotationEligibleForUnsavedCheck(
  ann: { annotationUID?: string; invalidated?: boolean; metadata?: { toolName?: string; graderId?: unknown } },
  deletedAnnotationUIDs: Set<string>,
  effectiveGraderId: string | null
): boolean {
  if (!ann.annotationUID || deletedAnnotationUIDs.has(ann.annotationUID)) {
    return false;
  }
  if (ann.invalidated === true) {
    return false;
  }
  const toolName = ann.metadata?.toolName;
  if (toolName === 'ReferenceLines' || toolName === 'Selection') {
    return false;
  }

  const annGraderId = ann.metadata?.graderId;
  if (effectiveGraderId != null && annGraderId != null) {
    if (String(annGraderId) !== effectiveGraderId) {
      return false;
    }
  }

  return true;
}

/**
 * True when a previously saved annotation was resized/moved on canvas but not saved yet.
 */
function hasUnsavedModifiedAnnotationsSync(): boolean {
  const modifiedAnnotationUIDs = (window as any).modifiedAnnotationUIDs as Set<string> | undefined;
  if (!modifiedAnnotationUIDs?.size) {
    return false;
  }

  const cornerstoneTools = (window as any).cornerstoneTools;
  if (!cornerstoneTools?.annotation?.state) {
    return false;
  }

  const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations() || [];
  const annotationByUid = new Map(
    allAnnotations.map((ann: { annotationUID: string }) => [ann.annotationUID, ann])
  );

  const deletedAnnotationUIDs: Set<string> =
    (window as any).deletedAnnotationUIDs || new Set<string>();
  const effectiveGraderId = resolveEffectiveGraderIdForUnsavedCheck();

  for (const uid of Array.from(modifiedAnnotationUIDs)) {
    const ann = annotationByUid.get(uid);
    if (!ann) {
      modifiedAnnotationUIDs.delete(uid);
      continue;
    }

    if (!isAnnotationEligibleForUnsavedCheck(ann, deletedAnnotationUIDs, effectiveGraderId)) {
      continue;
    }

    if (isAnnotationSaved(uid)) {
      return true;
    }

    // No longer saved (e.g. snapshot refreshed) — drop stale modified entry.
    modifiedAnnotationUIDs.delete(uid);
  }

  return false;
}

function hasTrackedMeasurementsSync(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return (window as any).__hasTrackedMeasurements === true;
}

/**
 * Synchronous check for beforeunload (browser refresh/close).
 * The canvas is considered unsaved only when the measurement tracking status is active.
 */
export function hasUnsavedNewAnnotationsSync(): boolean {
  try {
    if (!isLoggedInUserStudyGraderSync()) {
      return false;
    }

    return hasTrackedMeasurementsSync();
  } catch (error) {
    console.warn('[annotationSaveUtils] Error checking for unsaved new annotations (sync):', error);
    return false;
  }
}

/**
 * Returns true when the current user has tracked canvas annotation work not yet persisted.
 */
export async function hasUnsavedNewAnnotations(): Promise<boolean> {
  try {
    const { isLoggedInUserStudyGrader } = await import(
      '../../../../platform/app/src/utils/graderStudyViewOnly'
    );

    if (!(await isLoggedInUserStudyGrader())) {
      return false;
    }

    return hasUnsavedNewAnnotationsSync();
  } catch (error) {
    console.warn('[annotationSaveUtils] Error checking for unsaved new annotations:', error);
    return false;
  }
}

/**
 * Logs time spent on the current study (review or annotation mode).
 * Call before leaving the viewer or switching studies, regardless of unsaved annotations.
 */
export async function logViewerStudyActivityOnLeave(): Promise<void> {
  try {
    if (typeof window === 'undefined' || !window.location.pathname.startsWith('/viewer')) {
      return;
    }

    await ActivityService.getInstance().logActivity('exit');
  } catch (error) {
    console.warn('[annotationSaveUtils] Failed to log viewer activity on leave:', error);
  }
}

/**
 * Synchronous activity log for tab close / refresh (beforeunload / pagehide).
 */
export function logViewerStudyActivityOnUnloadSync(): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    const path = window.location.pathname || '';
    if (path !== '/viewer' && !path.endsWith('/viewer')) {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('StudyInstanceUIDs') && !params.has('studyinstanceuids')) {
        return;
      }
    }

    ActivityService.getInstance().logActivityOnPageUnload();
  } catch (error) {
    console.warn('[annotationSaveUtils] Failed to log viewer activity on unload:', error);
  }
}

/**
 * Saves draft annotations and logs activity (same APIs as floating-form Save Draft).
 */
export type SaveDraftAnnotationsResult =
  | { success: true; annotationId?: number }
  | { success: false };

export async function saveDraftAnnotationsWithActivity(): Promise<boolean> {
  const result = await saveDraftAnnotations();
  if (!result.success) {
    return false;
  }

  try {
    await ActivityService.getInstance().logActivity('save', result.annotationId);
  } catch {
    // Activity logging is non-blocking
  }

  return true;
}

function parseRawdataFromAnnotationRow(annotationRow: any): { annotations: any[] } | null {
  const annotationData = annotationRow?.data;
  if (!annotationData) {
    return null;
  }

  let annotationsData: { annotations?: any[] } | null = null;

  if (annotationData.rawdata) {
    annotationsData =
      typeof annotationData.rawdata === 'string'
        ? JSON.parse(annotationData.rawdata)
        : annotationData.rawdata;
  } else if (annotationData.annotations) {
    let parsed =
      typeof annotationData.annotations === 'string'
        ? JSON.parse(annotationData.annotations)
        : annotationData.annotations;
    if (
      parsed?.annotations &&
      typeof parsed.annotations === 'object' &&
      parsed.annotations.annotations
    ) {
      parsed = parsed.annotations;
    }
    annotationsData = parsed;
  }

  if (!annotationsData?.annotations || !Array.isArray(annotationsData.annotations)) {
    return null;
  }

  return { annotations: annotationsData.annotations };
}

/** UIDs present in any grader row of window.annotationsData (merged/admin-safe). */
export function getSavedAnnotationUidSet(): Set<string> {
  const uids = new Set<string>();

  try {
    const annotations = (window as any).annotationsData;

    if (!annotations?.is_success || !annotations?.data?.length) {
      return uids;
    }

    const rows = annotations.data[0]?.annotations;
    if (!Array.isArray(rows)) {
      return uids;
    }

    for (const row of rows) {
      const parsed = parseRawdataFromAnnotationRow(row);
      if (!parsed) {
        continue;
      }
      for (const ann of parsed.annotations) {
        if (ann?.annotationUID) {
          uids.add(ann.annotationUID);
        }
      }
    }
  } catch {
    // ignore
  }

  return uids;
}

export function isAnnotationSaved(annotationUID: string): boolean {
  try {
    return getSavedAnnotationUidSet().has(annotationUID);
  } catch (error) {
    console.warn('[annotationSaveUtils] Error checking if annotation is saved:', error);
    return false;
  }
}

/**
 * Captures Cornerstone annotations (similar to FloatingFormInline)
 * When graderId is provided, only captures annotations belonging to that grader
 * This prevents saving annotations from other graders when viewing merged annotations (admin/manager view)
 * @param graderId - Optional grader ID to filter annotations. If provided, only annotations with matching graderId (or no graderId for newly created) are included.
 * @returns Serialized annotations or null
 */
export async function captureCornerstoneAnnotations(graderId?: number | string | null): Promise<any> {
  try {
    // Import Cornerstone Tools dynamically
    const cornerstoneTools = (window as any).cornerstoneTools ||
      (await import('@cornerstonejs/tools')).default;

    if (!cornerstoneTools) {
      console.warn('[annotationSaveUtils] Cornerstone Tools not available');
      return null;
    }

    // Get all annotations from the annotation manager
    const allAnnotations = cornerstoneTools.annotation.state.getAllAnnotations();

    if (!allAnnotations || allAnnotations.length === 0) {
      return null;
    }

    // Get deleted annotation UIDs from global scope (if available)
    const deletedAnnotationUIDs = (window as any).deletedAnnotationUIDs || new Set<string>();

    // Normalize graderId to string for comparison
    const normalizedGraderId = graderId != null ? String(graderId) : null;

    // Filter out deleted annotations, invalidated annotations, ReferenceLines, and annotations from other graders
    const nonDeletedAnnotations = allAnnotations.filter((ann: any) => {
      if (deletedAnnotationUIDs.has(ann.annotationUID)) {
        return false;
      }
      if (ann.invalidated === true) {
        return false;
      }
      // Exclude ReferenceLines
      if (ann.metadata?.toolName === 'ReferenceLines') {
        return false;
      }

      // If graderId is provided, filter annotations by graderId
      // This is critical when admin/manager is viewing merged annotations from multiple graders
      // We only want to save annotations that belong to the current user
      if (normalizedGraderId != null) {
        const annGraderId = ann.metadata?.graderId;

        // If annotation has no graderId, it's a newly created annotation (belongs to current user)
        // Include it in the save
        if (!annGraderId) {
          console.log(`[annotationSaveUtils] Including annotation ${ann.annotationUID} (no graderId - newly created)`);
          return true;
        }

        // Normalize annotation's graderId for comparison
        const normalizedAnnGraderId = String(annGraderId);

        // Only include annotations that match the current user's graderId
        if (normalizedAnnGraderId === normalizedGraderId) {
          console.log(`[annotationSaveUtils] Including annotation ${ann.annotationUID} (graderId: ${normalizedAnnGraderId})`);
          return true;
        } else {
          // Exclude annotations from other graders
          console.log(`[annotationSaveUtils] Excluding annotation ${ann.annotationUID} (graderId: ${normalizedAnnGraderId}, expected: ${normalizedGraderId})`);
          return false;
        }
      }

      // If no graderId provided, include all annotations (backward compatibility)
      return true;
    });

    // Deduplicate annotations by annotationUID
    const seenUIDs = new Set<string>();
    const uniqueAnnotations = nonDeletedAnnotations.filter((ann: any) => {
      if (seenUIDs.has(ann.annotationUID)) {
        return false;
      }
      seenUIDs.add(ann.annotationUID);
      return true;
    });

    if (uniqueAnnotations.length === 0) {
      console.log(`[annotationSaveUtils] No annotations to save after filtering (graderId: ${normalizedGraderId})`);
      return null;
    }

    console.log(`[annotationSaveUtils] Capturing ${uniqueAnnotations.length} annotations for graderId: ${normalizedGraderId}`);

    // Serialize annotations for storage
   const serializedAnnotations = uniqueAnnotations.map((ann: any) => {
      // Clone data so we can normalize what we persist.
      // `data.color` is used elsewhere for per-grader coloring (merged/admin view).
      // For palette-selected per-annotation colors, we persist `data.annotationColor`
      // and avoid also saving `data.color` to prevent duplicate/conflicting fields.
      const dataToPersist = {
        ...(ann.data || {}),
      };

      if (dataToPersist.annotationColor != null) {
        delete (dataToPersist as any).color;
      }

      return ({
      annotationUID: ann.annotationUID,
      highlighted: ann.highlighted,
      isLocked: ann.isLocked,
      metadata: {
        toolName: ann.metadata.toolName,
        referencedImageId: ann.metadata.referencedImageId,
        FrameOfReferenceUID: ann.metadata.FrameOfReferenceUID,
        viewPlaneNormal: ann.metadata.viewPlaneNormal,
        viewUp: ann.metadata.viewUp,
        // Preserve graderId and graderColor if they exist (for newly created annotations, these may not be set yet)
        graderId: ann.metadata?.graderId,
        graderColor: ann.metadata?.graderColor,
      },
      data: {
        ...ann.data, // Spread all data properties to preserve complete structure including contour
      }
    });
  });

    return {
      annotations: serializedAnnotations,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('[annotationSaveUtils] Error capturing annotations:', error);
    return null;
  }
}

/**
 * Saves draft annotations to the database (similar to FloatingFormInline saveAnnotation)
 * This is called when deleting a measurement that was already saved
 */
export async function saveDraftAnnotations(): Promise<SaveDraftAnnotationsResult> {
  try {
    const activeStudyUid = await getActiveViewerStudyUid();

    // Get annotations data to extract study info
    const annotations = (window as any).annotationsData;

    if (!annotations || !annotations.is_success || !annotations.data || !annotations.data.length) {
      console.warn('[annotationSaveUtils] No annotations data available for save');
      return { success: false };
    }

    if (!isAnnotationResponseForStudyUid(annotations, activeStudyUid)) {
      console.warn(
        '[annotationSaveUtils] Refusing save: annotationsData is for a different study than the active viewer study',
        { activeStudyUid }
      );
      return { success: false };
    }

    const first = annotations.data[0] || {};
    const study = first.study || first.study_info || null;
    const anns = first.annotations || [];

    if (!study) {
      console.warn('[annotationSaveUtils] No study data available');
      return { success: false };
    }

    const studyId = study.id;

    // Logged-in user id (study often has no grader_id; match FloatingFormInline / floating form)
    let currentUserId: string | null = null;
    try {
      const { default: AuthService } = await import('../../../../platform/app/src/services/AuthService');
      const authService = AuthService.getInstance();
      const currentUser = authService.getCurrentUser();
      if (currentUser?.id != null) {
        currentUserId = String(currentUser.id);
      }
    } catch {
      // ignore
    }

    const ownAnnotation =
      currentUserId != null && anns?.length
        ? anns.find((ann: any) => String(ann.grader_id) === currentUserId)
        : null;

    const annotationRow = ownAnnotation ?? (anns && anns.length > 0 ? anns[0] : null);
    const savedAnnotationId = annotationRow?.id ?? null;

    let effectiveGraderId: number | string | null =
      (typeof window !== 'undefined' ? (window as any).currentUserGraderId : null) ??
      study.grader_id ??
      (first as any)?.study_info?.grader_id ??
      null;
    if (effectiveGraderId == null && currentUserId != null) {
      effectiveGraderId = currentUserId;
    }
    if (effectiveGraderId == null && annotationRow?.grader_id != null) {
      effectiveGraderId = annotationRow.grader_id;
    }

    const graderIdForPayload =
      effectiveGraderId != null ? Number(effectiveGraderId) : null;
    if (graderIdForPayload == null || !Number.isFinite(graderIdForPayload) || graderIdForPayload === 0) {
      console.warn('[annotationSaveUtils] Cannot save draft: grader_id could not be resolved');
      return { success: false };
    }

    // Extract existing parameters from the same annotation row we update
    let existingParameters: any[] = [];
    if (annotationRow?.parameters) {
      existingParameters = annotationRow.parameters
        .map((param: any) => ({
          field_id: param.field_id,
          value: param.value || '',
        }))
        .filter(
          (param: any) =>
            param.field_id !== null &&
            param.value !== '' &&
            param.value !== null &&
            param.value !== undefined
        );
    }

    // Get job_id from secure storage (encrypted)
    const { default: SecureStorageService } = await import('../../../../platform/app/src/services/SecureStorageService');
    const secureStorage = SecureStorageService.getInstance();
    let jobId: string | null = null;
    try {
      jobId = await secureStorage.getSessionItem('currentJobId');
    } catch (error) {
      // Fallback to unencrypted for migration
      jobId = sessionStorage.getItem('currentJobId');
    }
    const jobIdNumber = jobId ? parseInt(jobId, 10) : 0;

    // Import ApiService
    const { default: ApiService } = await import('../../../../platform/app/src/services/ApiService');
    const apiService = ApiService.getInstance();
    const baseUrl = apiService.getGradingServiceUrl();
    const endpoint = '/annotations';

    // Capture Cornerstone annotations before saving
    // Pass graderId to filter annotations - only save annotations belonging to the current user
    // This is critical when admin/manager is viewing merged annotations from multiple graders
    const cornerstoneAnnotations = await captureCornerstoneAnnotations(effectiveGraderId);

    // Prepare payload according to API structure
    const dataPayload = cornerstoneAnnotations
      ? { rawdata: cornerstoneAnnotations }
      : { rawdata: { annotations: [], timestamp: new Date().toISOString() } };

    const payload = {
      ...(savedAnnotationId && { annotation_id: savedAnnotationId }),
      study_id: studyId || 0,
      grader_id: graderIdForPayload,
      job_id: jobIdNumber,
      data: dataPayload,
      parameters: existingParameters, // Preserve existing parameters from saved annotation
      status: 'DRAFT',
    };

    // Make API call
    const response = await apiService.post(endpoint, payload, baseUrl);

    if (response.success && response.data) {
      clearAnnotationTrackingState();

      let annotationIdFromSave =
        extractAnnotationIdFromSaveResponse(response.data) ?? savedAnnotationId ?? undefined;

      if (!annotationIdFromSave) {
        let studyUid: string | null = study.study_uid || (window as any).currentStudyUid || null;
        if (!studyUid) {
          try {
            studyUid = await secureStorage.getSessionItem('currentStudyUid');
          } catch {
            studyUid = sessionStorage.getItem('currentStudyUid');
          }
        }
        if (studyUid) {
          annotationIdFromSave = await fetchAnnotationIdForGrader(
            studyUid,
            graderIdForPayload,
            jobIdNumber
          );
        }
      }

      // Reload annotation data to get updated information
      try {
        // Get study_uid from secure storage (encrypted)
        let studyUid: string | null = study.study_uid || (window as any).currentStudyUid || null;
        if (!studyUid) {
          try {
            studyUid = await secureStorage.getSessionItem('currentStudyUid');
          } catch (error) {
            studyUid = sessionStorage.getItem('currentStudyUid');
          }
        }
        if (studyUid) {
          // Clear pre-loaded data to force fresh API call
          (window as any).annotationsData = undefined;

          const endpoint = `/annotations/study/${studyUid}`;
          const queryParams: Record<string, any> = {};
          if (jobId && jobIdNumber !== 0) {
            queryParams.job_id = jobIdNumber;
          }

          const reloadResponse = await apiService.get(
            endpoint,
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
            baseUrl
          );

          if (reloadResponse.success && reloadResponse.data) {
            (window as any).annotationsData = reloadResponse.data;
            console.log('[annotationSaveUtils] Annotations reloaded after save');
          }
        }
      } catch (reloadError) {
        console.warn('[annotationSaveUtils] Error reloading annotations:', reloadError);
        // Don't fail the whole operation if reload fails
      }

      return { success: true, annotationId: annotationIdFromSave };
    } else {
      console.error('[annotationSaveUtils] Failed to save annotations:', response.error);
      return { success: false };
    }
  } catch (error) {
    console.error('[annotationSaveUtils] Error saving draft annotations:', error);
    return { success: false };
  }
}
